// Helper to parse IP from ICE candidate string
function _parseIpFromCandidate(candidateString: string): string | null {
  // Only interested in server reflexive (srflx) candidates
  if (!candidateString.includes('typ srflx')) {
    return null;
  }

  const parts = candidateString.split(' ');
  // The IP address is typically the 5th element (index 4)
  if (parts.length > 4) {
    const potentialIp = parts[4];

    // Basic validation for common IP formats
    const isIPv4 = /^[0-9]{1,3}(\.[0-9]{1,3}){3}$/.test(potentialIp);
    // Check if it contains ':' for IPv6 and isn't a link-local address
    const isIPv6 = /:/.test(potentialIp) && !potentialIp.startsWith('fe80::');

    if (isIPv4 || isIPv6) {
      return potentialIp;
    }
  }

  // Fallback if parsing fails
  console.warn(`[WARN] Could not parse IP from srflx candidate: ${candidateString}`);
  return null;
}

/**
 * Attempts to find the client's public IP address using WebRTC STUN servers.
 * @param timeoutMs - Timeout duration in milliseconds (default: 3000).
 * @returns A Promise that resolves with the IP address string or null if not found/error/timeout.
 */
export function getIpViaStun(timeoutMs = 3000): Promise<string | null> {
  return new Promise((resolve) => {
    if (typeof RTCPeerConnection === 'undefined') {
      console.warn('[WARN] WebRTC not supported, cannot get IP via STUN.');
      resolve(null);
      return;
    }

    let ip: string | null = null;
    let pc: RTCPeerConnection | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let resolved = false;

    const cleanupAndResolve = (result: string | null) => {
      if (resolved) return;
      resolved = true;

      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
      if (pc) {
        try {
          pc.close();
        } catch (e) {
          console.error('[ERROR] getIpViaStun: Error closing RTCPeerConnection:', e);
        }
        pc = null;
      }
      resolve(result);
    };

    const handleIceCandidate = (event: RTCPeerConnectionIceEvent) => {
      if (event.candidate?.candidate) {
        const foundIp = _parseIpFromCandidate(event.candidate.candidate);
        if (foundIp) {
          ip = foundIp;
          cleanupAndResolve(ip);
        }
      }
    };

    const handleIceGatheringStateChange = () => {
      if (pc?.iceGatheringState === 'complete' && !ip) {
        console.warn('[WARN] getIpViaStun: ICE gathering complete, no suitable srflx IP found.');
        cleanupAndResolve(null);
      }
    };

    const initiateIceGathering = (peerConnection: RTCPeerConnection) => {
      // Create a dummy data channel to trigger ICE candidate gathering
      peerConnection.createDataChannel('');

      // Create an offer to kickstart the process
      peerConnection
        .createOffer()
        .then((offer) => peerConnection.setLocalDescription(offer))
        .catch((err) => {
          console.error('[ERROR] getIpViaStun: Error creating offer/setting description:', err);
          cleanupAndResolve(null);
        });
    };

    try {
      pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });

      // Assign event listeners
      pc.onicecandidate = handleIceCandidate;
      pc.onicegatheringstatechange = handleIceGatheringStateChange;

      // Start the process
      initiateIceGathering(pc);

      // Set a timeout
      timeoutHandle = setTimeout(() => {
        if (!ip) {
          console.warn('[WARN] getIpViaStun: Timed out waiting for ICE candidate.');
          cleanupAndResolve(null);
        }
      }, timeoutMs);
    } catch (e) {
      console.error('[ERROR] getIpViaStun: Failed to initialize RTCPeerConnection:', e);
      cleanupAndResolve(null);
    }
  });
}
